import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  CourseListParams,
  CourseWriteInput,
  LessonListParams,
  LessonWriteInput,
  ModuleWriteInput,
  RescheduleInput,
  ScheduleFeedParams,
  ScheduleLessonInput,
} from './types';

/** Query-key factories — every curriculum key derives from these. */
export const courseKeys = {
  all: ['courses'] as const,
  lists: () => [...courseKeys.all, 'list'] as const,
  list: (params: CourseListParams) => [...courseKeys.lists(), params] as const,
  details: () => [...courseKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...courseKeys.details(), publicId] as const,
};

export const moduleKeys = {
  all: ['modules'] as const,
  list: (courseId: string) => [...moduleKeys.all, 'list', courseId] as const,
};

export const lessonKeys = {
  all: ['lessons'] as const,
  lists: () => [...lessonKeys.all, 'list'] as const,
  list: (params: LessonListParams) => [...lessonKeys.lists(), params] as const,
  details: () => [...lessonKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...lessonKeys.details(), publicId] as const,
};

export const scheduleKeys = {
  all: ['schedule'] as const,
  feeds: () => [...scheduleKeys.all, 'feed'] as const,
  feed: (params: ScheduleFeedParams) => [...scheduleKeys.feeds(), params] as const,
};

// ---------------------------------------------------------------------------
// Courses
// ---------------------------------------------------------------------------

export function useCourses(params: CourseListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: courseKeys.list(params),
    queryFn: () => api.getCourses(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useCourse(publicId: string) {
  return useQuery({
    queryKey: courseKeys.detail(publicId),
    queryFn: () => api.getCourse(publicId),
    enabled: Boolean(publicId),
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CourseWriteInput) => api.createCourse(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: courseKeys.lists() }),
  });
}

export function useUpdateCourse(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CourseWriteInput>) => api.updateCourse(publicId, input),
    onSuccess: course => {
      qc.setQueryData(courseKeys.detail(publicId), course);
      void qc.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

export function useArchiveCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveCourse(publicId),
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: courseKeys.lists() });
      void qc.invalidateQueries({ queryKey: courseKeys.detail(publicId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

export function useModules(courseId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: moduleKeys.list(courseId),
    queryFn: () => api.getModules(courseId),
    enabled: options.enabled ?? Boolean(courseId),
  });
}

export function useCreateModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ModuleWriteInput) => api.createModule(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: moduleKeys.list(courseId) });
      void qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) }); // counts
    },
  });
}

export function useUpdateModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: Partial<ModuleWriteInput> }) =>
      api.updateModule(publicId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: moduleKeys.list(courseId) }),
  });
}

/**
 * Delete a module. Its lessons are NOT deleted — `Lesson.module` is
 * `SET_NULL`, so they drop to course level (curriculum/models.py). The
 * course's lesson list is invalidated for exactly that reason.
 */
export function useDeleteModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteModule(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: moduleKeys.list(courseId) });
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
      void qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) }); // counts
    },
  });
}

export function useReorderModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, order }: { publicId: string; order: number }) =>
      api.reorderModule(publicId, order),
    onSuccess: () => void qc.invalidateQueries({ queryKey: moduleKeys.list(courseId) }),
  });
}

export function useReorderLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, order }: { publicId: string; order: number }) =>
      api.reorderLesson(publicId, order),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lessonKeys.lists() }),
  });
}

// ---------------------------------------------------------------------------
// Lessons
// ---------------------------------------------------------------------------

export function useLessons(params: LessonListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: lessonKeys.list(params),
    queryFn: () => api.getLessons(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useLesson(publicId: string) {
  return useQuery({
    queryKey: lessonKeys.detail(publicId),
    queryFn: () => api.getLesson(publicId),
    enabled: Boolean(publicId),
  });
}

export function useCreateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LessonWriteInput) => api.createLesson(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lessonKeys.lists() }),
  });
}

export function useUpdateLesson(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LessonWriteInput) => api.updateLesson(publicId, input),
    onSuccess: detail => {
      qc.setQueryData(lessonKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
    },
  });
}

export function useArchiveLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveLesson(publicId),
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
      void qc.invalidateQueries({ queryKey: lessonKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() });
    },
  });
}

export function usePublishLesson(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.publishLesson(publicId),
    onSuccess: detail => {
      qc.setQueryData(lessonKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
      void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() });
    },
  });
}

export function useUnpublishLesson(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.unpublishLesson(publicId),
    onSuccess: detail => {
      qc.setQueryData(lessonKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
      void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() });
    },
  });
}

export function useDuplicateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.duplicateLesson(publicId),
    onSuccess: copy => {
      qc.setQueryData(lessonKeys.detail(copy.public_id), copy);
      void qc.invalidateQueries({ queryKey: lessonKeys.lists() });
    },
  });
}

export function useScheduleLesson(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleLessonInput) => api.scheduleLesson(publicId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() }),
  });
}

// ---------------------------------------------------------------------------
// Assets — upload is called imperatively from the block editor (progress
// callback), so it stays a plain api call; deletion is exposed as a mutation.
// ---------------------------------------------------------------------------

export function useDeleteLessonAsset() {
  return useMutation({
    mutationFn: (publicId: string) => api.deleteLessonAsset(publicId),
  });
}

// ---------------------------------------------------------------------------
// Schedule / calendar
// ---------------------------------------------------------------------------

export function useScheduleFeed(
  params: ScheduleFeedParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: scheduleKeys.feed(params),
    queryFn: () => api.getScheduleFeed(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useRescheduleLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: RescheduleInput }) =>
      api.rescheduleLesson(publicId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() }),
  });
}

export function useUnscheduleLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.unscheduleLesson(publicId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKeys.feeds() }),
  });
}
